import React, { useEffect, useState } from "react";
import {
  Create,
  DataTable,
  List,
  Pagination,
  SimpleForm,
  TextField,
  SelectInput,
  NumberField,
  SimpleShowLayout,
  Show,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  useRecordContext,
  ReferenceArrayField,
  ReferenceField,
  FilterButton,
  useResourceDefinitions,
} from "react-admin";

import Card from "@mui/material/Card";
import { FileStatusField } from "./documents";
import { CustomDatetimeField } from "../lib/datetime";
import { DetailsButton } from "../lib/detailsButton";
import { LotNameField } from "./lots";

const taskOptions = [
  { id: "innovint_inbound", name: "InnoVint - Inbound" },
  /*
  Keep this here in case it will be used in the future.
  {
    id: "innovint_outbound_fso2",
    name: "InnoVint - Outbound Free SO2",
  },*/
  {
    id: "innovint_inbound_ferment_analysis",
    name: "InnoVint - Inbound Ferment Analysis",
  },
  {
    id: "innovint_outbound_virtual_composite",
    name: "InnoVint - Outbound Virtual Composite",
  },
  { id: "vintrace_inbound", name: "Vintrace - Inbound" },
  {
    id: "vintrace_get_work_orders",
    name: "Vintrace - Inbound Work Orders",
  },
  {
    id: "vintrace_get_transactions",
    name: "Vintrace - Inbound Transactions",
  },
  {
    id: "vintrace_inbound_ferment_analysis",
    name: "Vintrace - Inbound Ferment Analysis",
  },
  /*{ id: "vintrace_outbound_fso2", name: "Vintrace - Outbound Free SO2" },*/
  {
    id: "vintrace_outbound_virtual_composite",
    name: "Vintrace - Outbound Virtual Composite",
  },
  { id: "apply_additions_param_rules", name: "Apply Additions Param Rules" },
];

const taskOptionsAdmin = [
  {
    id: "barrelwise_blend_varietal_color_matching",
    name: "Ensure Blend Varietal Color Matching",
  },
  {
    id: "barrelwise_zero_capacity_vessel",
    name: "Find Zero Capacity Vessels",
  },
  {
    id: "barrelwise_water_use_sessions",
    name: "Aggregate Water Use Sessions",
  },
  {
    id: "barrelwise_iwbc_ignition_import",
    name: "Import IWBC Ignition",
  },
  {
    id: "barrelwise_smart_tanks_update",
    name: "Update Smart Tanks & Ferments",
  },
];

export const TaskList = () => {
  const { tasks } = useResourceDefinitions();
  const options = tasks?.options;

  const [taskFilters, setTaskFilters] = useState<React.ReactElement[]>([]);

  useEffect(() => {
    setTaskFilters([
      <SelectInput
        label="Type"
        source="type"
        choices={options?.adminSite ? taskOptionsAdmin : taskOptions}
        emptyText="All types"
      />,
    ]);
  }, [options, setTaskFilters]);

  return (
    <List
      perPage={100}
      pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
      exporter={false}
      actions={
        <TopToolbar>
          <FilterButton />
          <CreateButton />
          <ColumnsButton />
        </TopToolbar>
      }
      sort={{ field: "startTime", order: "DESC" }}
      storeKey={false}
      empty={false}
      filters={taskFilters}
    >
      <DataTable bulkActionButtons={false} rowClick={false}>
        <DataTable.Col source="id" label="ID" disableSort>
          <TextField source="id" label="ID" />
        </DataTable.Col>
        <DataTable.Col source="startTime" label="Time" disableSort>
          <CustomDatetimeField source="startTime" label="Time" />
        </DataTable.Col>
        <DataTable.Col source="type" label="Type" disableSort>
          <TextField source="type" label="Type" />
        </DataTable.Col>
        <DataTable.Col source="status" label="Status" disableSort>
          <FileStatusField source="status" />
        </DataTable.Col>
        <DataTable.Col source="runtime" label="Runtime" disableSort>
          <NumberField source="runtime" label="Runtime" />
        </DataTable.Col>
        <DataTable.Col>
          <DetailsButton />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

export const TaskCreate = () => {
  const { tasks } = useResourceDefinitions();
  const options = tasks?.options;

  return (
    <Create redirect="list">
      <SimpleForm>
        <SelectInput
          label="Type"
          source="type"
          choices={options?.adminSite ? taskOptionsAdmin : taskOptions}
        />
      </SimpleForm>
    </Create>
  );
};

export const GenericStatsBox = () => {
  const record = useRecordContext();

  if (!record.custom) return null;

  return (
    <Card variant="outlined" style={{ padding: 10, marginBottom: 50 }}>
      <h4>Custom Data</h4>
      <div>{JSON.stringify(record.custom)}</div>
    </Card>
  );
};

export const OutboundTaskStatsBox = () => {
  const record = useRecordContext();

  if (!record.custom) return null;

  return (
    <Card variant="outlined" style={{ padding: 10, marginBottom: 50 }}>
      <h4>Virtual Composites Synced</h4>
      {Array.isArray(record.custom.vcsSynced) ? (
        <ReferenceArrayField
          reference="virtual_composite"
          source="custom.vcsSynced"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="lotID" label="Lot" disableSort>
              <ReferenceField source="lotID" reference="lots" link="show">
                <LotNameField />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="creationTime" label="Submission Time">
              <CustomDatetimeField
                source="creationTime"
                label="Submission Time"
              />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.vcsSynced
      )}

      <h4>Lot Composites Synced</h4>
      {Array.isArray(record.custom.lcsSynced) ? (
        <ReferenceArrayField
          reference="events/fso2_reading"
          source="custom.lcsSynced"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="lotID" label="Lot" disableSort>
              <ReferenceField source="lotID" reference="lots" link="show">
                <LotNameField />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="datetime" label="Measurement Time">
              <CustomDatetimeField source="datetime" label="Measurement Time" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.lcsSynced
      )}
    </Card>
  );
};

export const InboundTaskStatsBox = () => {
  const record = useRecordContext();

  return (
    <Card variant="outlined" style={{ padding: 10, marginBottom: 50 }}>
      <h4>Blends Created</h4>
      {Array.isArray(record.custom.blendsCreated) ? (
        <ReferenceArrayField reference="blends" source="custom.blendsCreated">
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="name" label="Name">
              <TextField source="name" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.blendsCreated
      )}

      <h4>Blends Updated</h4>
      {Array.isArray(record.custom.blendsUpdated) ? (
        <ReferenceArrayField reference="blends" source="custom.blendsUpdated">
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="name" label="Name">
              <TextField source="name" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.blendsUpdated
      )}

      <h4>Blends Archived</h4>
      {Array.isArray(record.custom.blendsArchived) ? (
        <ReferenceArrayField
          reference="blends"
          source="custom.blendsArchived"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="name" label="Name">
              <TextField source="name" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.blendsArchived
      )}

      <h4>Lots Created</h4>
      {Array.isArray(record.custom.lotsCreated) ? (
        <ReferenceArrayField reference="lots" source="custom.lotsCreated">
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="name" label="Name">
              <TextField source="name" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.lotsCreated
      )}

      <h4>Lots Updated</h4>
      {Array.isArray(record.custom.lotsUpdated) ? (
        <ReferenceArrayField reference="lots" source="custom.lotsUpdated">
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="name" label="Name">
              <TextField source="name" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.lotsUpdated
      )}

      <h4>Lots Archived</h4>
      {Array.isArray(record.custom.lotsArchived) ? (
        <ReferenceArrayField reference="lots" source="custom.lotsArchived">
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="name" label="Name">
              <TextField source="name" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.lotsArchived
      )}

      <h4>Vessels Created</h4>
      {Array.isArray(record.custom.barrelsCreated) ? (
        <ReferenceArrayField
          reference="vessels"
          source="custom.barrelsCreated"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="barcode" label="Barcode">
              <TextField source="barcode" />
            </DataTable.Col>
            <DataTable.Col source="alias" label="Alias">
              <TextField source="alias" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.barrelsCreated
      )}

      <h4>Vessels Updated</h4>
      {Array.isArray(record.custom.barrelsUpdated) ? (
        <ReferenceArrayField
          reference="vessels"
          source="custom.barrelsUpdated"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="barcode" label="Barcode">
              <TextField source="barcode" />
            </DataTable.Col>
            <DataTable.Col source="alias" label="Alias">
              <TextField source="alias" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.barrelsUpdated
      )}

      <h4>Vessels Archived</h4>
      {Array.isArray(record.custom.barrelsArchived) ? (
        <ReferenceArrayField
          reference="vessels"
          source="custom.barrelsArchived"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="ID">
              <TextField source="id" label="ID" />
            </DataTable.Col>
            <DataTable.Col source="barcode" label="Barcode">
              <TextField source="barcode" />
            </DataTable.Col>
            <DataTable.Col source="alias" label="Alias">
              <TextField source="alias" />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      ) : (
        record.custom.barrelsArchived
      )}
    </Card>
  );
};

const StatsBox = () => {
  const record = useRecordContext();

  if (
    record.type === "apply_additions_param_rules" &&
    record.status === "success"
  ) {
    return <InboundTaskStatsBox />;
  }
  if (
    (record.type === "innovint_inbound" ||
      record.type === "vintrace_inbound") &&
    record.status === "success"
  ) {
    return <InboundTaskStatsBox />;
  }
  if (record.type.includes("outbound") && record.status === "success") {
    return <OutboundTaskStatsBox />;
  }
  if (record.type.includes("barrelwise_")) {
    return <GenericStatsBox />;
  }
};

export const TaskShow = () => {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="id" />
        <CustomDatetimeField source="startTime" />
        <TextField source="type" />
        <TextField source="status" />
        <TextField source="error" emptyText={"\u00A0"} />
        <TextField source="runtime" />
        <TextField
          source="custom.userInfo.name"
          label="API User Name"
          emptyText={"\u00A0"}
        />
        <TextField
          source="custom.userInfo.company"
          label="API User Company"
          emptyText={"\u00A0"}
        />
        <TextField
          source="custom.userInfo.email"
          label="API User Email"
          emptyText={"\u00A0"}
        />
        <StatsBox />
      </SimpleShowLayout>
    </Show>
  );
};
